import React, { useState } from "react"
import { Table, Skeleton, Button, message, Popconfirm, Checkbox } from "antd"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Link, useNavigate } from "react-router-dom"
import { ContainerFull } from "../components"
import { PlusOutlined, EditOutlined, DeleteOutlined } from "@ant-design/icons"
import { getFlowCells, deleteFlowCell, FlowCell } from "../endpoints"

export const FlowCells = (): JSX.Element => {
    const queryClient = useQueryClient()
    const navigate = useNavigate()
    const [showDeadFlowCells, setShowDeadFlowCells] = useState(false)
    const [showUnwashedFlowCells, setShowUnwashedFlowCells] = useState(false)
    const [showOnlyDeadOrUnwashedFlowCells, setShowOnlyDeadOrUnwashedFlowCells] = useState(false)

    const { isPending, isError, data, error } = useQuery({
        queryKey: ["flowcells"],
        queryFn: () => getFlowCells(),
        refetchOnMount: "always",
    })

    const deleteMutation = useMutation({
        mutationFn: ({ flowCellId }: { flowCellId: number }) => deleteFlowCell({ flowCellId }),
        onSuccess: () => {
            message.success("Flow cell deleted successfully")
            queryClient.invalidateQueries({ queryKey: ["flowcells"] })
        },
        onError: (error: any) => {
            message.error(error?.message || "Failed to delete flow cell")
        },
    })

    const handleCreate = () => {
        navigate('/admin/flowcell/new')
    }

    const handleEdit = (flowCellId: number) => {
        navigate(`/admin/flowcell/${flowCellId}`)
    }

    const handleDelete = (flowCellId: number) => {
        deleteMutation.mutate({ flowCellId })
    }

    if (isPending) return <Skeleton />
    if (isError || data.code !== 1000)
        return <div>Error: {error?.message || data?.message || "Unknown error"}</div>

    const getPoresLeft = (flowCell: FlowCell): number | null => {
        if (flowCell.poresLeft !== null && flowCell.poresLeft !== undefined) {
            return flowCell.poresLeft
        }
        if (flowCell.runs && flowCell.runs.length > 0 && flowCell.runs[0].pores != null) {
            return flowCell.runs[0].pores
        }
        return null
    }

    // Filter flow cells based on checkbox states
    const getFilteredFlowCells = () => {
        if (!data?.flowcells) return []
        
        let filtered = data.flowcells

        if (showOnlyDeadOrUnwashedFlowCells) {
            return filtered.filter((fc: FlowCell) => fc.diedAt || getPoresLeft(fc) === 0 || !fc.washed)
        }
        
        if (!showDeadFlowCells) {
            filtered = filtered.filter((fc: FlowCell) => {
                if (fc.diedAt) return false
                const pores = getPoresLeft(fc)
                // null means not set — don't treat as dead, keep it
                return pores === null || pores > 0
            })
        }
        
        if (!showUnwashedFlowCells) {
            filtered = filtered.filter((fc: FlowCell) => fc.washed)
        }
        
        return filtered
    }

    return (
        <ContainerFull style={{ fontFamily: '"Rubik", sans-serif' }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <h2>Flow Cells</h2>
                <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
                    Add Flow Cell
                </Button>
            </div>

            <div style={{ marginBottom: 16, display: "flex", gap: 16 }}>
                <Checkbox 
                    checked={showDeadFlowCells}
                    onChange={(e) => setShowDeadFlowCells(e.target.checked)}
                >
                    Show dead flow cells
                </Checkbox>
                <Checkbox 
                    checked={showUnwashedFlowCells}
                    onChange={(e) => setShowUnwashedFlowCells(e.target.checked)}
                >
                    Show unwashed flow cells
                </Checkbox>
                <Checkbox
                    checked={showOnlyDeadOrUnwashedFlowCells}
                    onChange={(e) => setShowOnlyDeadOrUnwashedFlowCells(e.target.checked)}
                >
                    Show only dead or unwashed flow cells
                </Checkbox>
            </div>

            <Table
                dataSource={getFilteredFlowCells()}
                rowKey="id"
                pagination={{ pageSize: 20 }}
                columns={[
                    {
                        title: "Actions",
                        key: "actions",
                        align: "center",
                        width: 120,
                        render: (_: any, record: FlowCell) => (
                            <div style={{ display: "flex", gap: 8, justifyContent: "center" }}>
                                <Button
                                    type="link"
                                    icon={<EditOutlined />}
                                    onClick={() => handleEdit(record.id)}
                                    size="small"
                                >
                                    
                                </Button>
                                <Popconfirm
                                    title="Delete flow cell"
                                    description="Are you sure you want to delete this flow cell?"
                                    onConfirm={() => handleDelete(record.id)}
                                    okText="Yes"
                                    cancelText="No"
                                >
                                    <Button
                                        type="link"
                                        danger
                                        icon={<DeleteOutlined />}
                                        size="small"
                                    >
                                        
                                    </Button>
                                </Popconfirm>
                            </div>
                        ),
                    },
                    {
                        title: "ID",
                        dataIndex: "id",
                        render: (id: number) => <Link to={`/admin/flowcell/${id}`}>{id}</Link>,
                        sorter: (a, b) => a.id - b.id,
                        width: 80,
                    },
                    {
                        title: "Name",
                        dataIndex: "name",
                        sorter: (a, b) => a.name.localeCompare(b.name),
                    },
                    {
                        title: "Pores",
                        dataIndex: "poresStartedWith",
                        align: "center",
                        render: (pores: number) => pores?.toLocaleString() || "-",
                    },
                    {
                        title: "Pores Left",
                        dataIndex: "runs",
                        align: "center",
                        render: (_: any, record: FlowCell) => {
                            if (record.diedAt) {
                                return <span style={{ color: "#ff4d4f" }}>0</span>
                            }
                            // Use poresLeft from flowcell record if not null, otherwise fall back to last run's pores
                            const poresLeft = record.poresLeft !== null && record.poresLeft !== undefined
                                ? record.poresLeft
                                : (record.runs && record.runs.length > 0 && record.runs[0].pores != null ? record.runs[0].pores : null)
                            if (poresLeft === null) return <span style={{ color: "#bbb" }}>-</span>
                            return poresLeft ? poresLeft.toLocaleString() : <span style={{ color: "#ff4d4f" }}>0</span>
                        },
                    },
                    {
                        title: "1st Use",
                        dataIndex: "startToUse",
                        render: (date: string) => (date ? new Date(date).toLocaleDateString() : "-"),
                    },
                    {
                        title: "Died On",
                        dataIndex: "diedAt",
                        render: (date: string) =>
                            date ? (
                                <span style={{ color: "#ff4d4f" }}>{new Date(date).toLocaleDateString()}</span>
                            ) : (
                                <span style={{ color: "#52c41a" }}>Active</span>
                            ),
                    },
                    {
                        title: "Washed",
                        dataIndex: "washed",
                        align: "center",
                        render: (washed: boolean) => (
                            washed ? 
                                <span style={{ color: "#52c41a", fontSize: "18px", fontWeight: "bold" }}>✓</span> : 
                                <span style={{ color: "#ff4d4f", fontSize: "18px", fontWeight: "bold" }}>✗</span>
                        ),
                    },
                    {
                        title: "Usage",
                        dataIndex: "runs",
                        render: (runs: any[]) => {
                            if (!runs || runs.length === 0) {
                                return <span style={{ color: "#bbb" }}>No usage history</span>
                            }
                            return (
                                <div>
                                    {runs.map((run, index) => (
                                        <div key={run.id} style={{ marginBottom: index < runs.length - 1 ? 4 : 0 }}>
                                            <Link to={`/admin/run/${run.id}`} style={{ fontWeight: 500 }}>
                                                #{run.id}
                                            </Link>
                                            {" | "}
                                            <span>{new Date(run.ts).toLocaleDateString()}</span>
                                            {" | "}
                                            <span>n: {run.nSamples || "N/A"}</span>
                                            {" | "}
                                            <span>Time: {run.duration ? `${run.duration}h` : "N/A"}</span>
                                            {" | "}
                                            <span>Reads: {run.nreads ? `${(run.nreads / 1000).toFixed(0)}k` : "N/A"}</span>
                                            {" | "}
                                            <span>Pores: {run.pores?.toLocaleString() || "N/A"}</span>

                                        </div>
                                    ))}
                                </div>
                            )
                        },
                    },
                    {
                        title: "Total Samples",
                        dataIndex: "runs",
                        align: "center",
                        render: (runs: any[]) => {
                            if (!runs || runs.length === 0) return <span style={{ color: "#bbb" }}>-</span>
                            const total = runs.reduce((sum, run) => sum + (run.nSamples || 0), 0)
                            return total > 0 ? total : <span style={{ color: "#bbb" }}>-</span>
                        },
                    },
                    {
                        title: "Notes",
                        dataIndex: "notes",
                        width: 200,
                        render: (notes: string) => (
                            <div style={{ 
                                maxWidth: 200, 
                                wordWrap: "break-word", 
                                whiteSpace: "normal" 
                            }}>
                                {notes || <span style={{ color: "#bbb" }}>-</span>}
                            </div>
                        ),
                    },
                ]}
            />
        </ContainerFull>
    )
}
